import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Space} from 'antd';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {StyledExpenses, StyledExpensesMoreButton, StyledExpensesContainer, StyledExpensesEmpty} from './StyledExpenses';
import {getExpensesTabColumns} from '../../ExpensesPage/ExpensesTab/ExpensesTabColumns';
import RightSideDrawer from '../../../RightSideDrawer';
import Table from '../../../Table';
import WebSocket from '../../../WebSocket';
import ExpenseAgentChat from '../../ExpensesPage/ExpenseAgentChat/ExpenseAgentChat';
import ExpenseDetailsPanel from '../../ExpensesPage/ExpenseDetailsPanel';
import ApproveExpenseModal from '../../ExpensesPage/ExpenseDetailsPanelTabs/tabComponents/ApproveExpenseModal';
import RejectExpenseModal from '../../ExpensesPage/ExpenseDetailsPanelTabs/tabComponents/RejectExpenseModal';
import {ExpensesIcon} from '../../../../icons';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {
  backendWebsocketActionsConstants,
  DEFAULT_MODAL_PROPS,
  expensePolicyStatusConstants,
  expenseReviewStatusConstants
} from '../../../../constants';
import {useIsCompanyChanged} from '../../../../hooks';
import {expensesHelpers, objectHelpers, transactionsHelpers} from '../../../../utils/helpers';
import {ReactComponent as EmptySvgImage} from '../../../../static/images/pages/overview/empty-expenses.svg';


const tableKeys = {
  NEED_YOUR_APPROVAL: 'need-your-approval',
  NEED_YOUR_INPUT: 'need-your-input',
  LAST_10_EXPENSES: 'last-10-expenses',
}

const { NEED_YOUR_APPROVAL, NEED_YOUR_INPUT, LAST_10_EXPENSES } = tableKeys;

const defaultTableProps = {
  data: [],
  loading: false,
  pagination: undefined
}

const defaultModalProps = {
  ...DEFAULT_MODAL_PROPS,
  tableKey: undefined
}

const Expenses = ({
  dispatch,
  approveExpense,
  getExpense,
  getExpenses,
  reassignExpense,
  reopenExpense,
  rejectExpense,
  unlinkExpense,
  updateExpense,
  isAdmin,
  isOpenAgentChat,
  employeeEmail,

  handleOpenDetails,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [approvalTableProps, setApprovalTableProps] = useState(defaultTableProps);
  const [inputTableProps, setInputTableProps] = useState(defaultTableProps);
  const [lastExpensesTableProps, setLastExpensesTableProps] = useState(defaultTableProps);
  const [enabledWebSocketConnection, setEnabledWebSocketConnection] = useState(false);

  const [detailsWindowProps, setDetailsWindowProps] = useState(defaultModalProps);
  const [approveModalProps, setApproveModalProps] = useState(defaultModalProps);
  const [rejectModalProps, setRejectModalProps] = useState(defaultModalProps);

  const companyId = useIsCompanyChanged();

  const isEmpty = useMemo(() => {
    const noData = !approvalTableProps.data.length && !inputTableProps.data.length && !lastExpensesTableProps.data.length;
    const notLoading = !approveModalProps.loading && !inputTableProps.loading && !lastExpensesTableProps.loading;
    return noData && notLoading;
  }, [approvalTableProps, inputTableProps, lastExpensesTableProps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpenAgentChat && !enabledWebSocketConnection) {
      setEnabledWebSocketConnection(true);
    }
  }, [isOpenAgentChat]); // eslint-disable-line react-hooks/exhaustive-deps

  const getQueryParams = (key) => ({
    [NEED_YOUR_APPROVAL]: {
      approver_email: [employeeEmail, 'any_admin'],
      per_page: 3,
      review_status: expenseReviewStatusConstants.IN_REVIEW
    },
    [NEED_YOUR_INPUT]: {
      per_page: 3,
      policy_status: expensePolicyStatusConstants.MISSING_INFO,
      reported_by: employeeEmail,
      review_status: expenseReviewStatusConstants.IN_REVIEW
    },
    [LAST_10_EXPENSES]: {
      per_page: 10,
      reported_by: isAdmin ? undefined : employeeEmail
    },
  }[key] || {});

  const loadTableData = ({key, setter, query, initial = false} = {}) => {
    setter?.(prevState => ({...prevState, loading: true}));

    getExpenses({
      query: {...getQueryParams(key), order_by: '-date', ...query},
      successCallback: (data) => {
        const results = data?.results || [];
        setter?.(prevState => ({
          ...prevState,
          data: initial ? results : [...prevState.data, ...results],
          loading: false,
          pagination: data.pagination
        }));
      },
      errorCallback: () => {
        setter?.(prevState => ({
          ...prevState,
          loading: false,
          pagination: undefined
        }));
      }
    })
  }

  const getTableState = (key) => ({
    [LAST_10_EXPENSES]: {state: lastExpensesTableProps, setState: setLastExpensesTableProps},
    [NEED_YOUR_APPROVAL]: {state: approvalTableProps, setState: setApprovalTableProps},
    [NEED_YOUR_INPUT]: {state: inputTableProps, setState: setInputTableProps},
  }[key] || undefined);

  const updateTableData = ({tableKey, initial = false} = {}) => {
    const props = {
      [LAST_10_EXPENSES]: {key: LAST_10_EXPENSES, setter: setLastExpensesTableProps},
      [NEED_YOUR_APPROVAL]: {key: NEED_YOUR_APPROVAL, setter: setApprovalTableProps},
      [NEED_YOUR_INPUT]: {key: NEED_YOUR_INPUT, setter: setInputTableProps},
    }[tableKey] || undefined;
    if (!props) return;

    loadTableData({...props, query: initial ? { cursor: undefined} : {}, initial});
  }

  useEffect(() => {
    if (!companyId) return;

    loadTableData({key: LAST_10_EXPENSES, setter: setLastExpensesTableProps, initial: true});
    loadTableData({key: NEED_YOUR_INPUT, setter: setInputTableProps, initial: true});
    if (isAdmin) {
      loadTableData({key: NEED_YOUR_APPROVAL, setter: setApprovalTableProps, initial: true});
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = (key, cursor) => {
    const setter = {
      [NEED_YOUR_APPROVAL]: setApprovalTableProps,
      [NEED_YOUR_INPUT]: setInputTableProps,
    }[key] || undefined
    if (!setter) return;

    loadTableData({key, setter, query: { cursor }})
  }

  const getLabel = (key) => (
    <Space size='small' align='center'>
      <ExpensesIcon /><span>{t(`overviewPage.expenses.${key}`)}</span>
    </Space>
  );

  const handleRowClick = (record, tableKey) => {
    if (detailsWindowProps?.expense?.id === record.id) return;

    setDetailsWindowProps({
      ...defaultModalProps,
      index: `${record.id}_${tableKey}`,
      open: true,
      expense: record,
      tableKey,
    });
    handleOpenDetails?.(true);
  }

  const handleApproveTableExpense = ({e, expense, tableKey} = {}) => {
    e?.stopPropagation();

    const canQuickApprove = expensesHelpers.canQuickApprove({expense, employeeEmail});
    if (canQuickApprove) {
      handleApproveExpense({e, expense, tableKey});
    } else {
      setApproveModalProps({
        ...DEFAULT_MODAL_PROPS,
        open: true,
        expense,
        tableKey
      });
    }
  }

  const handleOpenRejectModal = ({e, expense, data = {}, tableKey} = {}) => {
    e?.stopPropagation();
    setRejectModalProps({
      ...DEFAULT_MODAL_PROPS,
      open: true,
      expense,
      data,
      tableKey
    });
  }

  const getTableColumns = (tableKey) => {
    return getExpensesTabColumns({
      t,
      isAdmin,
      employeeEmail,
      onApprove: (props) => handleApproveTableExpense({...props, tableKey}),
      onReject: (props) => handleOpenRejectModal({...props, tableKey}),
    }).map(c => ({...c, sorter: false}));
  }

  const getRowClassName = (record, key) => {
    return transactionsHelpers.getRowClassName({
      index: `${record.id}_${key}`,
      record,
      selectedRowIndex: detailsWindowProps?.index,
    });
  };

  const getTable = ({key, tableProps, showSeeMoreButton = true} = {}) => {
    const cursor = tableProps?.pagination?.cursor;
    const isAvailableSeeMore = showSeeMoreButton && cursor;
    return (
      <Space size='small' direction='vertical'>
        <Table
          columns={getTableColumns(key)}
          pagination={false}
          dataSource={tableProps?.data || []}
          onRow={(record) => ({
            onClick: () => handleRowClick(record, key)
          })}
          rowKey='id'
          rowClassName={(record) => getRowClassName(record, key)}
        />
        {isAvailableSeeMore && (
          <StyledExpensesMoreButton
            loading={tableProps?.loading}
            onClick={() => handleLoadMore(key, cursor)}
            type='link'
          >
            {t('see')} {t('more')}
          </StyledExpensesMoreButton>
        )}
      </Space>
    )
  }

  const handleCloseApproveModal = () => setApproveModalProps({...approveModalProps, open: false});

  const handleCloseRejectModal = () => setRejectModalProps({...rejectModalProps, open: false});

  const updateTableAndDetails = ({id, response, tableKey, forceUpdate = false} = {}) => {
    const {state, setState} = getTableState(tableKey) || {};
    const expenseFromTable = state?.data.find(e => e.id === id);
    if (forceUpdate || (expenseFromTable && expenseFromTable?.review_status !== response?.review_status)) {
      updateTableData({tableKey: LAST_10_EXPENSES, initial: true});
      updateTableData({tableKey: NEED_YOUR_INPUT, initial: true});
      if (isAdmin) {
        updateTableData({tableKey: NEED_YOUR_APPROVAL, initial: true});
      }
    } else {
      setState(prevState => ({
        ...prevState,
        data: prevState.data.map(exp => exp.id === id ? response : exp)
      }));
    }
    if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
      setDetailsWindowProps(prevState => ({ ...prevState, expense: response }));
    }
  };

  const handleApproveDetailsExpense = ({e, expense, data}) => {
    const action = data?.admin_override ? handleApproveTableExpense : handleApproveExpense;
    action({e, expense});
  }

  const handleApproveExpense = ({e, expense, data = {}, successCallback, errorCallback, tableKey}) => {
    e?.stopPropagation();

    const id = expense?.id;
    if (!id) return;

    approveExpense({
      id,
      data,
      successCallback: (response) => {
        updateTableAndDetails({id, response, tableKey: tableKey || approveModalProps.tableKey});
        successCallback?.(response);
      },
      errorCallback: (response) => {
        expensesHelpers.handleExpenseError({response, fallbackKEy: 'approveExpenseError', t});
        errorCallback?.(response);
      },
    });
  }

  const handleReviewEdit = () => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    reopenExpense({
      id,
      successCallback: (response) => updateTableAndDetails({id, response, forceUpdate: true, tableKey: detailsWindowProps.tableKey}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKEy: 'reopenExpenseError', t}),
    });
  }

  const handleReassignExpense = (e, data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    reassignExpense({
      id,
      data,
      successCallback: (response) => updateTableAndDetails({id, response, tableKey: detailsWindowProps.tableKey}),
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'reassignExpenseError', t}),
    });
  }

  const handleOkRejectModal = ({fields, successCallback, errorCallback}) => {
    const id = rejectModalProps?.expense?.id;
    if (!id) return;

    setRejectModalProps(prevState => ({ ...prevState, loading: true }));

    rejectExpense({
      id,
      data: {
        ...fields,
        ...(rejectModalProps?.data || {})
      },
      successCallback: (response) => {
        setRejectModalProps(defaultModalProps);
        updateTableAndDetails({id, response, tableKey: rejectModalProps.tableKey});
        successCallback?.(response);
      },
      errorCallback: (err) => {
        setRejectModalProps(prevState => ({ ...prevState, loading: false }));
        errorCallback?.(err?.response);
      },
    });
  }

  const handleOkApproveModal = ({successCallback, errorCallback}) => {
    const expense = approveModalProps?.expense;
    if (!expense) return;

    setApproveModalProps(prevState => ({ ...prevState, loading: true }));
    handleApproveExpense({
      expense,
      data: { admin_override: true },
      successCallback: (response) => {
        successCallback?.(response);
        handleCloseApproveModal();
      },
      errorCallback: (response) => {
        setApproveModalProps(prevState => ({ ...prevState, loading: false }));
        errorCallback?.(response);
      }
    });
  }

  const handleEditExpense = (data) => {
    const id = detailsWindowProps?.expense?.id;
    if (!id) return;

    updateExpense({
      id,
      data,
      successCallback: () => {
        const expenseId = detailsWindowProps?.expense?.id;
        if (detailsWindowProps.open && expenseId === id) {
          setDetailsWindowProps(prevState => ({
            ...prevState,
            expense: { ...prevState.expense, ...data }
          }));

          const updateTableState = (tableProps, setTableProps) => {
            if (tableProps?.data.some(exp => exp.id === expenseId)) {
              setTableProps({
                ...tableProps,
                data: tableProps.data.map(exp => exp.id === expenseId ? { ...exp, ...data } : exp)
              });
            }
          };

          updateTableState(approvalTableProps, setApprovalTableProps);
          updateTableState(inputTableProps, setInputTableProps);
          updateTableState(lastExpensesTableProps, setLastExpensesTableProps);
        }
      },
      errorCallback: (response) => expensesHelpers.handleExpenseError({response, fallbackKey: 'updateExpenseError', t}),
    })
  }

  const handleUpdateExpense = (updatedExpense) => {
    const selectedExpense = detailsWindowProps?.expense;
    const id = selectedExpense?.id;
    if (!id) return;

    if (updatedExpense) {
      const tableKey = detailsWindowProps?.tableKey;
      const {setState} = getTableState(tableKey) || {};
      if (detailsWindowProps.open && detailsWindowProps?.expense?.id === id) {
        setDetailsWindowProps(prevState => ({ ...prevState, expense: { ...prevState.expense, ...updatedExpense } }));
      }
      setState(prevState => ({
        ...prevState,
        data: prevState?.data.map(exp => exp.id === id ? {...exp, ...updatedExpense} : exp)
      }));
    }
  }

  const handleInvoiceRemove = (successCallback, errorCallback) => {
    transactionsHelpers.removeInvoiceFromTransaction({
      t,
      transaction: {expense: detailsWindowProps?.expense},
      successCallback,
      errorCallback,
      unlinkExpense,
      onExpenseUpdate: handleUpdateExpense
    });
  }

  const handleCloseDetailsModal = () => {
    setDetailsWindowProps({...detailsWindowProps, open: false, expense: null, index: null});
    handleOpenDetails?.(false);
  }

  const handleWebsocketMessage = (data) => {
    const getResponseProp = (key) => objectHelpers.getObjProp(data, key);
    const action = getResponseProp('action');

    if (!action) return;

    ({
      [backendWebsocketActionsConstants.EXPENSE_SUBMITTED]: () => {
        loadTableData({key: LAST_10_EXPENSES, setter: setLastExpensesTableProps, initial: true});
        loadTableData({key: NEED_YOUR_INPUT, setter: setInputTableProps, initial: true});
      },
      [backendWebsocketActionsConstants.EXPENSE_FILL_MISSING_INFO]: () => {
        const id = getResponseProp('expense_id') || getResponseProp('expense');
        if (!id) return;
        getExpense({
          id,
          successCallback: (response) => {
            updateTableAndDetails({id, response, forceUpdate: true, tableKey: detailsWindowProps.tableKey});
          }
        })
      }
    })[action]?.();
  };

  const items = [
    (approvalTableProps.data.length > 0 && isAdmin) && {
      children: getTable({key: NEED_YOUR_APPROVAL, tableProps: approvalTableProps}),
      key: NEED_YOUR_APPROVAL,
      label: getLabel('needYourApproval'),
    },
    inputTableProps.data.length > 0 && {
      children: getTable({key: NEED_YOUR_INPUT, tableProps: inputTableProps}),
      key: NEED_YOUR_INPUT,
      label: getLabel('needYourInput'),
    },
    lastExpensesTableProps.data.length > 0 && {
      children: getTable({key: LAST_10_EXPENSES, tableProps: lastExpensesTableProps, showSeeMoreButton: false}),
      key: LAST_10_EXPENSES,
      label: getLabel('last10Expenses'),
    }
  ].filter(Boolean);

  return (
    <StyledExpensesContainer>

      <StyledExpenses ghost items={items} defaultActiveKey={Object.values(tableKeys)} {...rest} />

      {isEmpty && (
        <StyledExpensesEmpty
          image={<EmptySvgImage />}
          description={t('overviewPage.expenses.empty.description')}
          title={t('overviewPage.expenses.empty.title')}
        />
      )}

      <RightSideDrawer open={detailsWindowProps.open} top={90} boxShadow='none'>
        <ExpenseDetailsPanel
          {...detailsWindowProps}
          className='expense-details-panel'
          onApprove={handleApproveDetailsExpense}
          onClose={handleCloseDetailsModal}
          onEdit={handleEditExpense}
          onInvoiceRemove={handleInvoiceRemove}
          onUpdate={handleUpdateExpense}
          onReassign={handleReassignExpense}
          onReject={({e, data = {}}) => handleOpenRejectModal({data, e, expense: detailsWindowProps.expense, tableKey: detailsWindowProps.tableKey})}
          onReviewEdit={handleReviewEdit}
        />
      </RightSideDrawer>

      <RejectExpenseModal
        {...rejectModalProps}
        handleCancel={handleCloseRejectModal}
        handleOk={handleOkRejectModal}
      />

      <ApproveExpenseModal
        {...approveModalProps}
        handleCancel={handleCloseApproveModal}
        handleOk={handleOkApproveModal}
      />

      <ExpenseAgentChat />

      {enabledWebSocketConnection && <WebSocket onMessage={handleWebsocketMessage} />}

    </StyledExpensesContainer>
  );
}

Expenses.propTypes = {
  handleOpenDetails: PropTypes.func,
}

const mapStateToProps = ({
  expense: { agentChatProps },
  user: { isAdmin, employee }
}) => ({
  isAdmin,
  isOpenAgentChat: agentChatProps.open,
  employeeEmail: employee?.email
});

const mapDispatchToProps = {
  approveExpense: expenseActions.approveExpense,
  getExpense: expenseActions.getExpense,
  getExpenses: expenseActions.getExpenses,
  reassignExpense: expenseActions.reassignExpense,
  rejectExpense: expenseActions.rejectExpense,
  reopenExpense: expenseActions.reopenExpense,
  unlinkExpense: invoicesActions.unlinkExpense,
  updateExpense: expenseActions.updateExpense,
}

export default connect(mapStateToProps, mapDispatchToProps)(Expenses);
